/**
 * Carrier portal token verification.
 *
 * Lands here from the magic-link email at /carrier/verify?token=...
 * On mount, auto-POSTs the token to /api/carrier-auth/verify-token, and on
 * success stores the session JWT and redirects to /carrier/dashboard.
 *
 * On failure (expired, already-used, not-found), shows the backend's error
 * message and a link back to /carrier/login.
 *
 * IMPORTANT: this page calls verify-token ONCE. The token is single-use,
 * so a refresh of this page after a successful verify will fail with
 * 'already used' — but by then the JWT is in localStorage and the user is
 * on /carrier/dashboard anyway. The 'already used' path is mostly for cases
 * like 'user clicked the link, closed the tab without finishing, clicked
 * the same link again' (which is rare).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { carrierAuthVerifyToken } from '@/api/client';
import { useCarrierSession, writePortalAccounts, accountLabel, type CarrierSession } from '@/auth/useCarrierSession';

export function CarrierVerifyPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setSession } = useCarrierSession();

    const [state, setState] = useState<'verifying' | 'success' | 'error' | 'choose'>('verifying');
    /** ⭐ F215: one email, several accounts — the person picks which portal to open */
    const [choices, setChoices] = useState<CarrierSession[]>([]);
    const openAccount = (a: CarrierSession) => {
        setSession(a);
        setState('success');
        setTimeout(() => navigate(a.kind === 'Client' ? '/client/dashboard' : '/carrier/dashboard', { replace: true }), 400);
    };
    const [error, setError] = useState<string | null>(null);
    const verifiedRef = useRef(false);

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setError('No sign-in token in the URL. Try requesting a new link.');
            setState('error');
            return;
        }

        // Guard against React Strict Mode running effects twice in dev:
        // the second run would call verify-token on a now-consumed token
        // and fail with 'already used' even though the first succeeded.
        if (verifiedRef.current) return;
        verifiedRef.current = true;

        (async () => {
            try {
                const result = await carrierAuthVerifyToken(token);
                // ⭐ F215: several accounts under this email → store them all and ask which to open
                const accounts: CarrierSession[] = (result.accounts ?? []).map((a) => ({
                    sessionToken: a.sessionToken, expiresAt: a.expiresAt, displayName: a.displayName,
                    carrierCode: a.carrierCode ?? '', email: a.email, kind: a.kind,
                    clientCode: a.clientCode ?? null, clientName: a.clientName ?? null,
                }));
                writePortalAccounts(accounts);
                if (accounts.length > 1) {
                    setChoices(accounts);
                    setState('choose');
                    return;
                }
                // ⭐ F212: one sign-in for both portals — the answer says which one this is
                const kind = result.kind === 'Client' ? 'Client' : 'Carrier';
                setSession({
                    sessionToken: result.sessionToken,
                    expiresAt: result.expiresAt,
                    displayName: result.displayName,
                    carrierCode: result.carrierCode ?? '',
                    email: result.email,
                    kind,
                    clientCode: result.clientCode ?? null,
                    clientName: result.clientName ?? null,
                });
                setState('success');
                // Brief delay so users see "Signed in!" before redirect.
                setTimeout(() => {
                    navigate(kind === 'Client' ? '/client/dashboard' : '/carrier/dashboard', { replace: true });
                }, 700);
            } catch (err: any) {
                setError(err.message ?? 'Sign-in failed.');
                setState('error');
            }
        })();
    }, [searchParams, setSession, navigate]);

    return (
        <div className="min-h-[calc(100vh-64px)] grid place-items-center px-4 py-12">
            <div className="w-full max-w-md text-center">
                {state === 'verifying' && (
                    <>
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Signing you in&hellip;</p>
                    </>
                )}

                {state === 'choose' && (
                    <>
                        <h1 className="page-title page-title-compact mb-2">Which account?</h1>
                        <p className="text-sm text-muted-foreground mb-6">
                            Your email signs in to {choices.length} portal accounts. You can switch later from the top right.
                        </p>
                        <div className="grid gap-2">
                            {choices.map((a) => (
                                <Button key={a.sessionToken.slice(-16)} variant="outline" className="justify-start" onClick={() => openAccount(a)}>
                                    {accountLabel(a)}
                                </Button>
                            ))}
                        </div>
                    </>
                )}

                {state === 'success' && (
                    <>
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-pipeline-emerald-bg text-pipeline-emerald-text text-xl">
                            ✓
                        </div>
                        <p className="text-sm text-muted-foreground">Signed in. Redirecting&hellip;</p>
                    </>
                )}

                {state === 'error' && (
                    <>
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-pipeline-rose-bg text-pipeline-rose-text">
                            <AlertCircle className="h-6 w-6" />
                        </div>
                        <h1 className="page-title page-title-compact mb-2">Sign-in failed</h1>
                        <p className="text-sm text-muted-foreground mb-8">
                            {error}
                        </p>
                        <Button onClick={() => navigate(window.location.pathname.startsWith('/client') ? '/client/login' : '/carrier/login', { replace: true })} variant="outline">
                            Back to sign-in
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
